import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { get } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import facilityService from "../../services/facility";
import iconService from "../../services/icon";
import queryDefault from "../../utils/queryDefault";

export default function FacilityEdit() {
  const notification = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);
  const schema = yup.object().shape({
    name: yup
      .string()
      .min(2)
      .required("name is a required field and length > 2"),
  });
  const form = useForm({
    defaultValues: {
      id: "",
      name: "",
      admin_name: user.username,
      type: "",
      logo: "",
    },
    resolver: yupResolver(schema),
  });

  const [filter, setFilter] = useState("");

  const getLogoList = useQuery(
    ["icons"],
    () => {
      return iconService.getAllIcon();
    },
    {
      ...queryDefault,
    }
  );

  const updateFacility = useMutation(
    "edit-facility",
    facilityService.updateFacility,
    {
      onSuccess: () => {
        navigate("/app/properties/facility");

        notification.onOpen({
          message: "Facility was successfully updated!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed Update Facility",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const onSubmit = (values: any) => {
    updateFacility.mutate(values);
  };

  useEffect(() => {
    if (id) {
      facilityService.getFacilityById(Number(id)).then(({ data }) => {

        form.setValue("id", id);
        form.setValue("name", data.name);
        form.setValue("admin_name", user.username);
        form.setValue("type", data.type);
        setFilter(data.logo);
        form.setValue("logo", data.logo);
      });
    }
  }, [id, form, user.username]);

  const handleLogoChange = useCallback((newValue: any) => {
    setFilter(newValue?.name || "");
    const logos = get(getLogoList, "data.data", []);
    const matchedIcon = logos.find((icon: any) => icon.name === newValue.name);
    if (matchedIcon) {
      form.setValue("logo", newValue ? newValue.name : "");
    }
  }, [getLogoList, form]);

  useEffect(() => handleLogoChange({ name: filter }), [filter, handleLogoChange]);

  return (
    <MainLayout>
      <Breadcrumb
        label={`Edit ${form.getValues("name")}`}
        breadcrumbs={[
          { label: "Facility", href: "/app/properties/facility" },
          { label: "Edit", href: `/app/properties/facility/${id}` },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="facility"
        requiredPermissions={{ is_update: true, is_read: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Logo</FormLabel>
                <Controller
                  name="logo"
                  control={form.control}
                  render={({ field }) => (
                    <Autocomplete
                      options={get(getLogoList, "data.data", [])}
                      getOptionLabel={(option: any) => option.name}
                      inputValue={filter}
                      onInputChange={(event, newInputValue) => setFilter(newInputValue)}
                      onChange={(event, newValue) => handleLogoChange(newValue)}
                      filterOptions={(options, { inputValue }) =>
                        options.filter((option) =>
                          option.name
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Icon"
                          variant="outlined"
                        />
                      )}
                      noOptionsText="No icons available"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Type</FormLabel>
                <Controller
                  name="type"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value="Property" key="Property">
                          Property
                        </MenuItem>
                        <MenuItem value="Room" key="Room">
                          Room
                        </MenuItem>
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={updateFacility.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
